package com.steamstreet.vegasful.browser.components

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.Element
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLElement
import kotlin.math.PI
import kotlin.math.floor
import kotlin.math.max
import kotlin.math.min
import kotlin.math.round
import kotlin.math.sin
import kotlin.random.Random

/** Everything the canvas needs to paint one sky: two colours, and how much night is in it. */
private class Sky(val mid: DoubleArray, val horizon: DoubleArray, val stars: Double, val city: Double) {
    fun copy() = Sky(mid.copyOf(), horizon.copyOf(), stars, city)
}

private class Light(
    var x: Double, val y: Double, val r: Double, val colour: DoubleArray, val alpha: Double,
    val phase: Double, val speed: Double, val vx: Double, val takesAccent: Boolean
)

private class SkyStar(val x: Double, val y: Double, val size: Double, val phase: Double, val speed: Double)

private class Spark(
    var x: Double, var y: Double, var vx: Double, var vy: Double,
    var life: Double, val max: Double, val size: Double, val colour: String
)

/**
 * The neon sky behind the home hero and the concierge page: soft, out-of-focus city lights over a
 * sky that drifts through the brand's neon colours, with the real Las Vegas hour as a tint.
 *
 * [canvas] is painted to fill [root], and fades into the page at the bottom. Painting stops while
 * the tab is hidden or [root] is scrolled away, and a visitor who asks for reduced motion gets one
 * still frame.
 *
 * Callers can name a sky to [show] in place of the hour's, lend the lights an [accent] colour,
 * set off a [burst] of sparkles, and make the sky [excited]: quicker and brighter, which the
 * concierge uses while it works.
 */
class NeonSky(private val root: HTMLElement, private val canvas: HTMLCanvasElement) {
    private val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D

    val reducedMotion: Boolean = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    /** Called after the canvas has been resized to [root], for callers that measure with it. */
    var onResize: () -> Unit = {}

    private var width = 0.0
    private var height = 0.0
    private var lights = listOf<Light>()
    private var stars = listOf<SkyStar>()
    private val sparks = mutableListOf<Spark>()

    private var current = SKIES.getValue("night").copy()
    private var target = current.copy()
    private var accent = doubleArrayOf(255.0, 79.0, 245.0)
    private var accentTarget = 0.0
    private var accentAmount = 0.0
    private var excitement = 0.0
    private var excitementTarget = 0.0

    /** The sky named by [show], which the hour does not replace until [followHour] is called. */
    private var pinned: String? = null

    private var frameId = 0
    private var previous = 0.0
    private var clock = 12.0
    private var visible = true

    fun start() {
        current = SKIES.getValue(phaseOf(vegasNow())).copy()
        target = current.copy()
        resize()
        observe()

        // The sky follows the real hour, so it is checked again every minute.
        window.setInterval({ if (pinned == null) followHour() }, 60_000)

        if (reducedMotion) drawStill() else startLoop()
    }

    /** Whether there is a sky called [name] to [show]. */
    fun hasSky(name: String): Boolean = SKIES.containsKey(name)

    /** Change to the sky called [name], and hold it until [followHour] is called. */
    fun show(name: String) {
        pinned = name
        target = SKIES[name]?.copy() ?: return
        if (reducedMotion) drawStill()
    }

    /** Return to the sky of the current Las Vegas hour. */
    fun followHour() {
        pinned = null
        target = SKIES.getValue(phaseOf(vegasNow())).copy()
        if (reducedMotion) drawStill()
    }

    /** Lend about half the lights [colour], or hand them back to the drifting sky with null. */
    fun accent(colour: DoubleArray?) {
        if (colour != null) {
            accent = colour
            accentTarget = 1.0
        } else {
            accentTarget = 0.0
        }
    }

    /**
     * An excited sky drifts about two and a half times as fast and its lights burn brighter. The
     * change is eased in and out, so turning it on and off never jumps.
     */
    var excited: Boolean = false
        set(value) {
            field = value
            excitementTarget = if (value) 1.0 else 0.0
        }

    private fun resize() {
        val context = ctx ?: return
        val rect = root.getBoundingClientRect()
        width = rect.width
        height = rect.height
        val dpr = min(window.devicePixelRatio, 2.0)
        canvas.width = max(1, round(width * dpr).toInt())
        canvas.height = max(1, round(height * dpr).toInt())
        context.setTransform(dpr, 0.0, 0.0, dpr, 0.0, 0.0)

        val random = Random(11)
        val count = max(16.0, min(38.0, width / 30)).toInt()
        val scale = if (width < 640) .65 else 1.0
        lights = (0 until count).map { i ->
            val depth = random.nextDouble()
            Light(
                x = random.nextDouble() * width,
                y = height * (.42 + random.nextDouble() * .6),
                r = (7 + depth * depth * 44) * scale,
                colour = PALETTE[i % PALETTE.size],
                alpha = .16 + (1 - depth) * .26,
                phase = random.nextDouble() * 2 * PI,
                speed = .25 + random.nextDouble() * .6,
                vx = (random.nextDouble() - .5) * (4 + depth * 8),
                takesAccent = random.nextDouble() < .45
            )
        }
        stars = (0 until 60).map {
            SkyStar(
                random.nextDouble() * width, random.nextDouble() * height * .45,
                if (random.nextDouble() < .15) 2.0 else 1.3,
                random.nextDouble() * 2 * PI, .6 + random.nextDouble() * 1.8
            )
        }
        onResize()
        if (reducedMotion || frameId == 0) drawStill()
    }

    private fun drawStill() {
        current = target.copy()
        accentAmount = accentTarget
        paint(clock, 0.0)
    }

    private fun paint(t: Double, dt: Double) {
        val c = ctx ?: return
        val k = min(1.0, dt * 2)
        for (i in 0..2) {
            current.mid[i] += (target.mid[i] - current.mid[i]) * k
            current.horizon[i] += (target.horizon[i] - current.horizon[i]) * k
        }
        current = Sky(current.mid, current.horizon,
            current.stars + (target.stars - current.stars) * k,
            current.city + (target.city - current.city) * k)
        accentAmount += (accentTarget - accentAmount) * min(1.0, dt * 2.5)

        c.globalCompositeOperation = "source-over"
        c.globalAlpha = 1.0
        c.clearRect(0.0, 0.0, width, height)

        // The colour of the hour builds gradually from the top down. The top is the brand purple
        // tinted with the hour, dark enough for the white headline but not black, and the horizon
        // is softened towards the mid colour so the band behind the box is a glow, not a stripe.
        //
        // The sky never sits still: the colours drift slowly through the brand's neon palette and
        // the glow breathes, with the hour (or a named sky) as a tint on top.
        val ambient = ambientColour(t)
        val pulse = .84 + .16 * sin(t * .7) + .12 * excitement
        val mid = mix(current.mid, mix(ambient, BRAND, .45), .5)
        val top = mix(BRAND, mid, .42)
        val horizon = scale(mix(mix(current.horizon, mid, .25), ambient, .6), pulse)
        val sky = c.createLinearGradient(0.0, 0.0, 0.0, height)
        sky.addColorStop(0.0, rgb(top))
        sky.addColorStop(.32, rgb(mix(top, mid, .45)))
        sky.addColorStop(.6, rgb(mix(mid, horizon, .55)))
        sky.addColorStop(.8, rgb(horizon))
        sky.addColorStop(1.0, rgb(mix(horizon, GROUND, .6)))
        c.fillStyle = sky
        c.fillRect(0.0, 0.0, width, height)

        if (current.stars > .02) {
            c.fillStyle = "#fff"
            stars.forEach { s ->
                c.globalAlpha = current.stars * (.15 + .5 * (.5 + .5 * sin(t * s.speed + s.phase)))
                c.fillRect(s.x, s.y, s.size, s.size)
            }
            c.globalAlpha = 1.0
        }

        // Soft, out-of-focus city lights, brighter at the rim the way a lens draws them.
        c.globalCompositeOperation = "lighter"
        val drift = dt * (1 + 2 * excitement)
        val brightness = 1 + .35 * excitement
        lights.forEach { light ->
            light.x += light.vx * drift
            if (light.x < -light.r) light.x = width + light.r else if (light.x > width + light.r) light.x = -light.r
            // The lights that take an accent follow the drifting sky at rest and the accent
            // colour while one is lent, blending from one to the other as it comes and goes.
            val colour = if (light.takesAccent) {
                mix(light.colour, mix(ambient, accent, accentAmount), .45 + .4 * accentAmount)
            } else {
                light.colour
            }
            val a = min(1.0, light.alpha * brightness) * (.3 + .7 * current.city) *
                (.72 + .28 * sin(t * light.speed + light.phase))
            val y = light.y + sin(t * .2 + light.phase) * 4
            val g = c.createRadialGradient(light.x, y, 0.0, light.x, y, light.r)
            g.addColorStop(0.0, rgba(colour, a * .55))
            g.addColorStop(.72, rgba(colour, a * .5))
            g.addColorStop(.9, rgba(colour, a * .95))
            g.addColorStop(1.0, rgba(colour, 0.0))
            c.fillStyle = g
            c.beginPath()
            c.arc(light.x, y, light.r, 0.0, 2 * PI)
            c.fill()
        }
        c.globalCompositeOperation = "source-over"

        // Fade into the page, so the hero meets the panels below it without an edge. The fade is
        // long and eased, so it reads as the light falling off rather than a shutter coming down.
        val fadeTop = height * .62
        val fade = c.createLinearGradient(0.0, fadeTop, 0.0, height)
        fade.addColorStop(0.0, rgba(GROUND, 0.0))
        fade.addColorStop(.55, rgba(GROUND, .35))
        fade.addColorStop(1.0, rgba(GROUND, 1.0))
        c.fillStyle = fade
        c.fillRect(0.0, fadeTop, width, height - fadeTop)

        c.globalCompositeOperation = "lighter"
        paintSparks(c, dt)
        c.globalCompositeOperation = "source-over"
    }

    private fun paintSparks(c: CanvasRenderingContext2D, dt: Double) {
        val iterator = sparks.iterator()
        while (iterator.hasNext()) {
            val s = iterator.next()
            s.life -= dt
            if (s.life <= 0) {
                iterator.remove()
                continue
            }
            s.vy += 150 * dt
            s.x += s.vx * dt
            s.y += s.vy * dt
            s.vx *= .985
            val size = s.size * (.5 + s.life / s.max)
            c.globalAlpha = min(1.0, s.life / s.max * 1.4)
            c.fillStyle = s.colour
            c.beginPath()
            c.moveTo(s.x, s.y - size)
            c.quadraticCurveTo(s.x, s.y, s.x + size, s.y)
            c.quadraticCurveTo(s.x, s.y, s.x, s.y + size)
            c.quadraticCurveTo(s.x, s.y, s.x - size, s.y)
            c.quadraticCurveTo(s.x, s.y, s.x, s.y - size)
            c.fill()
        }
        c.globalAlpha = 1.0
    }

    /** A small burst of sparkles from [from], which must lie over [root]. */
    fun burst(from: Element) {
        if (reducedMotion) return
        val r = from.getBoundingClientRect()
        val h = root.getBoundingClientRect()
        val x = r.left + r.width / 2 - h.left
        val y = r.top + r.height / 2 - h.top
        repeat(28) { i ->
            val angle = Random.nextDouble() * 2 * PI
            val speed = 70 + Random.nextDouble() * 180
            val life = .7 + Random.nextDouble() * .6
            sparks += Spark(
                x, y, kotlin.math.cos(angle) * speed, sin(angle) * speed - 40, life, life,
                2 + Random.nextDouble() * 3.2, SPARK_COLOURS[i % SPARK_COLOURS.size]
            )
        }
        startLoop()
    }

    private fun startLoop() {
        if (reducedMotion || frameId != 0 || !visible || document.asDynamic().hidden == true) return
        previous = window.performance.now()
        frameId = window.requestAnimationFrame(::frame)
    }

    private fun stopLoop() {
        if (frameId != 0) window.cancelAnimationFrame(frameId)
        frameId = 0
    }

    private fun frame(now: Double) {
        frameId = window.requestAnimationFrame(::frame)
        val dt = min(.05, (now - previous) / 1000).takeIf { it > 0 } ?: .016
        previous = now
        excitement += (excitementTarget - excitement) * min(1.0, dt * 1.5)
        // Time itself runs faster in an excited sky, which quickens the drift, the breathing and
        // the twinkle together rather than any one of them on its own.
        clock += dt * (1 + 1.5 * excitement)
        paint(clock, dt)
    }

    /**
     * Keep the canvas sized to [root], and stop painting whenever nobody can see it: the tab is
     * in the background, or [root] has scrolled away.
     */
    private fun observe() {
        val reflect = js("Reflect")
        val resizeObserver = window.asDynamic().ResizeObserver
        if (resizeObserver != undefined) {
            reflect.construct(resizeObserver, arrayOf<dynamic>({ _: dynamic -> resize() })).observe(root)
        } else {
            window.addEventListener("resize", { resize() })
        }

        val intersectionObserver = window.asDynamic().IntersectionObserver
        if (intersectionObserver != undefined) {
            reflect.construct(intersectionObserver, arrayOf<dynamic>({ entries: dynamic ->
                visible = entries[0].isIntersecting as Boolean
                if (visible) startLoop() else stopLoop()
            })).observe(root)
        }
        document.addEventListener("visibilitychange", {
            if (document.asDynamic().hidden == true) stopLoop() else startLoop()
        })
    }
}

// -------------------------------------------------------------------- palettes

private fun sky(mid: IntArray, horizon: IntArray, stars: Double, city: Double) =
    Sky(mid.map { it.toDouble() }.toDoubleArray(), horizon.map { it.toDouble() }.toDoubleArray(), stars, city)

/**
 * The skies: the hours of the day, and one for each of the home headline's flip words. Only the
 * lower part of the canvas takes these colours.
 */
private val SKIES = mapOf(
    "night" to sky(intArrayOf(34, 22, 104), intArrayOf(70, 46, 168), 1.0, .9),
    "sunset" to sky(intArrayOf(132, 36, 110), intArrayOf(246, 108, 104), .15, .6),
    "dinner" to sky(intArrayOf(100, 26, 112), intArrayOf(255, 88, 118), .45, .85),
    "weekend" to sky(intArrayOf(96, 12, 124), intArrayOf(232, 40, 178), .8, 1.0),
    "dawn" to sky(intArrayOf(76, 54, 144), intArrayOf(240, 150, 168), .15, .35),
    "day" to sky(intArrayOf(62, 78, 176), intArrayOf(150, 180, 244), 0.0, .3),
    "golden" to sky(intArrayOf(116, 50, 120), intArrayOf(236, 128, 132), 0.0, .5),
    "late" to sky(intArrayOf(28, 12, 78), intArrayOf(104, 24, 122), 1.0, 1.0)
)

private val PALETTE = listOf(
    doubleArrayOf(255.0, 194.0, 122.0), doubleArrayOf(255.0, 111.0, 232.0), doubleArrayOf(111.0, 220.0, 255.0),
    doubleArrayOf(169.0, 139.0, 255.0), doubleArrayOf(255.0, 111.0, 143.0), doubleArrayOf(255.0, 214.0, 160.0)
)

/** The page background, which the sky fades into. */
private val GROUND = doubleArrayOf(21.0, 1.0, 54.0)

/** The brand purple the top of the sky starts from. */
private val BRAND = doubleArrayOf(36.0, 10.0, 84.0)

private val SPARK_COLOURS = listOf("#ff7dfe", "#ffffff", "#99ebff", "#ffd36b")

/**
 * The brand colours the sky drifts through, one full cycle every [AMBIENT_CYCLE_S] seconds.
 */
private val AMBIENT = listOf(
    doubleArrayOf(206.0, 46.0, 196.0),
    doubleArrayOf(118.0, 64.0, 224.0),
    doubleArrayOf(56.0, 118.0, 232.0),
    doubleArrayOf(236.0, 72.0, 146.0)
)
private const val AMBIENT_CYCLE_S = 20.0

/** Where the drift is at [t] seconds, eased between neighbouring colours. */
private fun ambientColour(t: Double): DoubleArray {
    val position = (t / AMBIENT_CYCLE_S * AMBIENT.size) % AMBIENT.size
    val index = floor(position).toInt()
    val fraction = position - index
    val eased = fraction * fraction * (3 - 2 * fraction)
    return mix(AMBIENT[index], AMBIENT[(index + 1) % AMBIENT.size], eased)
}

private fun scale(c: DoubleArray, k: Double) = doubleArrayOf(min(255.0, c[0] * k), min(255.0, c[1] * k), min(255.0, c[2] * k))

private fun mix(a: DoubleArray, b: DoubleArray, k: Double) =
    doubleArrayOf(a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k)

private fun rgb(c: DoubleArray) = "rgb(${c[0].toInt()},${c[1].toInt()},${c[2].toInt()})"

private fun rgba(c: DoubleArray, a: Double) =
    "rgba(${c[0].toInt()},${c[1].toInt()},${c[2].toInt()},${(a * 1000).toInt() / 1000.0})"
